<template>
  <div class="product-thumbnail">
    <div class="thumbnail-container">
      <b-img
        :key="imageKey"
        :src="fullImageUrl"
        thumbnail
        fluid
        class="mx-auto product-image"
        :alt="productName || 'Imagem do Produto'"
        @click="showFullscreen = true"
        style="cursor: pointer"
      />
      <div class="upload-button-container">
        <b-button
          variant="primary"
          size="sm"
          class="mt-2"
          @click="triggerFileUpload"
          :disabled="uploading"
        >
          <b-spinner v-if="uploading" small></b-spinner>
          <b-icon v-else icon="upload"></b-icon>
          {{ uploading ? 'Enviando...' : 'Alterar Imagem' }}
        </b-button>
      </div>
    </div>
    <input
      ref="fileInput"
      type="file"
      accept="image/*"
      class="d-none"
      @change="handleFileUpload"
    />

    <!-- Modal de Visualização em Tela Cheia -->
    <div v-if="showFullscreen" class="fullscreen-modal" @click.self="showFullscreen = false">
      <div class="fullscreen-content">
        <button class="close-button" @click="showFullscreen = false">
          <b-icon icon="x" font-scale="1.5"></b-icon>
        </button>
        <img :src="fullImageUrl" :alt="productName || 'Imagem do Produto'" class="fullscreen-image" />
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import logout from '@/helpers/LogoutHelper';

export default {
  name: 'ProductThumbnail',
  
  props: {
    productId: {
      type: [Number, String],
      required: true,
      default: 0
    },
    productName: {
      type: String,
      default: ''
    },
    currentImageUrl: {
      type: String,
      default: ''
    }
  },
  
  data() {
    return {
      imageUrl: this.currentImageUrl,
      defaultImage: `${process.env.VUE_APP_ROOT_API}/defaultimage.jpg`,
      uploading: false,
      messageShow: false,
      messageText: '',
      messageVariant: '',
      showFullscreen: false,
      imageKey: 0
    };
  },

  computed: {
    fullImageUrl() {
      const baseUrl = process.env.VUE_APP_ROOT_API || 'http://localhost:8082';
      const imageUrl = this.imageUrl ? `${baseUrl}${this.imageUrl}` : this.defaultImage;
      
      // Adiciona timestamp como query parameter para evitar cache
      return `${imageUrl}?t=${this.imageKey}`;
    }
  },

  methods: {
    triggerFileUpload() {
      this.$refs.fileInput.click();
    },
    
    async handleFileUpload(event) {
      const file = event.target.files[0];
      if (!file) return;
      
      // Validação de tipo e tamanho
      if (!file.type.includes('image/')) {
        this.showMessage('Por favor, selecione apenas arquivos de imagem.', 'danger');
        return;
      }
      
      if (file.size > process.env.VUE_APP_IMAGE_UPLOAD_LIMIT * 1024 * 1024) {
        this.showMessage(`Verifique o tamanho da imagem. A imagem deve ser menor que ${process.env.VUE_APP_IMAGE_UPLOAD_LIMIT} MB.`, 'danger');
        return;
      }

      if (!this.productId){
        // Emitir evento para salvar o produto primeiro
        this.$emit('save-product-first', {
          file: file,
          callback: (savedProductId) => {
            if (savedProductId) {
              // Se o produto foi salvo, tenta fazer upload da imagem
              this.uploadImage(file, savedProductId);
            }
          }
        });
        return;
      }
      
      await this.uploadImage(file, this.productId);
    },

    async uploadImage(file, productId) {
      this.uploading = true;
      
      try {
        const formData = new FormData();
        formData.append('image', file);
        formData.append('productId', productId);
        
        const response = await axios.post(
          `${process.env.VUE_APP_ROOT_API}/products/upload-image`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
              Authorization: `Bearer ${localStorage.getItem('token')}`
            }
          }
        );
        
        if (response.status === 200 || response.status === 201) {
          this.imageUrl = response.data.imageUrl;
          this.imageKey++;
          this.showMessage('Imagem atualizada com sucesso!', 'success');
          this.$emit('image-updated', response.data.imageUrl);
        }
      } catch (error) {
        console.error('Erro ao fazer upload da imagem:', error);
        
        if (error.response) {
          if (error.response.status === 401) {
            logout();
          } else if (error.response.status === 413) {
            console.log(error.response.data.message);
            this.showMessage(`A imagem é muito grande. O limite é ${process.env.VUE_APP_IMAGE_UPLOAD_LIMIT} MB.`, 'danger');
          } else {
            this.showMessage(
              error.response.data.message || 'Erro ao fazer upload da imagem.',
              'danger'
            );
          }
        } else {
          this.showMessage('Erro ao conectar com o servidor.', 'danger');
        }
      } finally {
        this.uploading = false;
        this.$refs.fileInput.value = '';
      }
    },
    
    showMessage(text, variant) {
      this.messageText = text;
      this.messageVariant = variant;
      this.messageShow = true;
      
      // Emitir mensagem para componente pai
      this.$emit('show-message', {
        text,
        variant,
        timeout: 5
      });
      
      // Opcional: esconder mensagem após alguns segundos
      setTimeout(() => {
        this.messageShow = false;
      }, 5000);
    }
  },

  watch: {
    currentImageUrl(newValue) {
      this.imageUrl = newValue;
      this.imageKey++;
    }
  }
};
</script>

<style scoped>
.product-thumbnail {
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 200px;
  margin: 0 auto;
}

.thumbnail-container {
  position: relative;
  width: 100%;
  max-width: 200px;
}

.product-image {
  width: 100%;
  height: auto;
  max-height: 200px;
  object-fit: contain;
}

.upload-button-container {
  text-align: center;
  margin-top: 5px;
}

/* Estilos para o modal de tela cheia */
.fullscreen-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.9);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.fullscreen-content {
  position: relative;
  width: 90%;
  height: 90%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.fullscreen-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.close-button {
  position: absolute;
  top: 15px;
  right: 15px;
  background-color: rgba(255, 255, 255, 0.3);
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  color: white;
  transition: background-color 0.3s;
}

.close-button:hover {
  background-color: rgba(255, 255, 255, 0.5);
}
</style>